// CANCEL-004 — confirmation dialog for "Branch confirmed? Mark as cancelled".
//
// Only shown after the engineer has already sent a cancellation request. The
// dialog makes the engineer explicitly confirm that the branch has told them
// the order is cancelled, since this flips the status pill to Cancelled and
// hides the manual Mark Completed / Mark Declined buttons.
//
// Ported from octopus-mock/components/orders/cancel-request.tsx
// (MarkCancelledDialog).
import * as Dialog from '@radix-ui/react-dialog'
import { Loader2 } from 'lucide-react'
import { Button } from '../../radix'

interface MarkCancelledDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  isPending: boolean
}

export function MarkCancelledDialog({
  open,
  onClose,
  onConfirm,
  isPending,
}: MarkCancelledDialogProps) {
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next && !isPending) onClose()
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content
          onPointerDownOutside={(event) => {
            if (isPending) event.preventDefault()
          }}
          onEscapeKeyDown={(event) => {
            if (isPending) event.preventDefault()
          }}
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90vw] max-w-md bg-white rounded-xl border border-gray-200 shadow-xl p-4 sm:p-5"
        >
          <Dialog.Title className="text-lg font-bold text-gray-900">
            Mark as cancelled?
          </Dialog.Title>
          <Dialog.Description className="mt-2 text-sm text-gray-700">
            Only do this once the branch has told you the order is cancelled.
            It will show as Cancelled in My orders.
          </Dialog.Description>
          <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isPending}
              className="min-h-[44px] w-full sm:w-auto"
            >
              Go back
            </Button>
            <Button
              onClick={onConfirm}
              disabled={isPending}
              className="min-h-[44px] w-full sm:w-auto"
            >
              {isPending && <Loader2 className="w-4 h-4 motion-safe:animate-spin" />}
              Mark as cancelled
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
